import { Component, HostListener, Inject, Input, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ApplicationFacade } from '@ridango/components';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Temporal } from '@js-temporal/polyfill';
import { IonModal, PopoverController } from '@ionic/angular';
import { DepartureArrivalTime, BaseComponent } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, delay, filter, retryWhen, switchMap, take, takeUntil } from 'rxjs/operators';
import { AppStartupService } from '@ridango/core/lib/services';
import { of, throwError, Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'ridango-ui-datetime-select',
    templateUrl: './datetime-select.component.html',
    styleUrls: ['./datetime-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: DatetimeSelectComponent
        }
    ]
})
export class DatetimeSelectComponent extends BaseComponent implements ControlValueAccessor, OnInit, OnDestroy {
    private selectTimeButton: HTMLElement;
    private timePickerCloseBtn: HTMLIonButtonElement;
    private modalScrollPosition = 0;
    private readonly boundMonthYearEventListener = this.monthYearEventListener.bind(this);
    @Input() triggerId: string;
    @Input() dateOnly = false;
    @Input() showDepartArriveToggle = false;
    @Input() minDate: string;
    @Input() maxDate: string;
    @Input() resetFilter = false;
    @Input() forceNativeDatePresentation = false;

    @ViewChild(IonModal) timeModal: IonModal;

    assetsPath$ = this.applicationFacade.assetsPath$;
    popoverObserver: MutationObserver | null = null;
    mo: MutationObserver | null = null;
    internalDepartureArrivalVal: DepartureArrivalTime;
    externalDepartureArrivalVal: DepartureArrivalTime;

    touched = false;
    disabled = false;
    locale: string;
    timezone: string = this.appConfig.getTimezone();
    isContrast$ = this.applicationFacade.highContrast$;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChange: (val: DepartureArrivalTime) => void = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onTouched: () => void = () => {};

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.popoverController.dismiss();
        }
    }

    constructor(
        @Inject('ENV') public env,
        private applicationFacade: ApplicationFacade,
        private popoverController: PopoverController,
        private translate: TranslateService,
        private appConfig: AppStartupService
    ) {
        super();
    }

    ngOnInit() {
        this.locale = this.translate.store.currentLang || this.translate.store.defaultLang;
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.locale = event.lang;
        });
    }

    // TODO: Remove this logic when https://github.com/ionic-team/ionic-framework/issues/25859 is fixed
    private pollForButtons(): void {
        of(null)
            .pipe(
                switchMap(() => {
                    const datetimeElement = document.querySelector('ion-datetime');
                    const nextButton = this.findButtonInNestedShadows(
                        datetimeElement?.shadowRoot,
                        'button.button-native[aria-label="next month"]'
                    );
                    const prevButton = this.findButtonInNestedShadows(
                        datetimeElement?.shadowRoot,
                        'button.button-native[aria-label="previous month"]'
                    );

                    return nextButton && prevButton
                        ? of({
                              nextButton,
                              prevButton
                          })
                        : throwError(() => new Error('Buttons not found'));
                }),
                retryWhen((errors) => errors.pipe(delay(200), take(10))),
                filter((buttons): buttons is { nextButton: HTMLElement; prevButton: HTMLElement } => buttons !== null),
                catchError(() => of(null))
            )
            .subscribe((buttons) => {
                if (buttons) {
                    buttons.nextButton.setAttribute('aria-label', this.translate.instant('DATE_PICKER_NEXT_MONTH'));
                    buttons.prevButton.setAttribute('aria-label', this.translate.instant('DATE_PICKER_PREVIOUS_MONTH'));
                    this.setTimeBodyAriaLabel();
                }
            });
    }

    private setTimeBodyAriaLabel(): void {
        const datetimeElement = document.querySelector('ion-datetime');
        const timeBodyButton = this.findButtonInNestedShadows(datetimeElement?.shadowRoot, 'button.time-body');

        if (!timeBodyButton) {
            return;
        }

        const buttonText = timeBodyButton.textContent?.trim();
        if (this.internalDepartureArrivalVal.type === 'arriveBy') {
            timeBodyButton.setAttribute(
                'aria-label',
                `${this.translate.instant('PLANNER_PRIORITY_ARRIVAL')} ${buttonText}`
            );
        } else if (this.internalDepartureArrivalVal.type === 'leaveAfter') {
            timeBodyButton.setAttribute(
                'aria-label',
                `${this.translate.instant('PLANNER_PRIORITY_DEPARTURE')} ${buttonText}`
            );
        }
    }

    // FIXME: Remove after upgrading to Ionic 7+
    private applyTimePickerStyles() {
        this.applicationFacade.highContrast$.pipe(take(1)).subscribe((isContrast) => {
            document.querySelectorAll('ion-datetime').forEach((dt) => {
                const shadowRoot = dt.shadowRoot;
                const popover = shadowRoot?.querySelector('ion-popover');
                const picker = popover?.querySelector('ion-picker-internal');
                if (!shadowRoot || !popover || !picker) {
                    return;
                }

                if (isContrast) {
                    const dateTimeStyle = document.createElement('style');
                    dateTimeStyle.textContent = `
                    .time-body {
                        color: black !important;
                    }
                `;
                    shadowRoot.prepend(dateTimeStyle);
                    picker.style.setProperty('background-color', '#000000');
                    const s = document.createElement('style');
                    s.textContent = `
                    .picker-highlight {
                        background-color: var(--ion-color-secondary) !important;
                    }
                    .picker-after,.picker-before {
                    display: none !important;
                    }
                  `;
                    picker.shadowRoot?.prepend(s);
                }

                popover.querySelectorAll('ion-picker-column-internal').forEach((col) => {
                    if (!col.shadowRoot) {
                        return;
                    }

                    const styleEl = document.createElement('style');
                    styleEl.textContent = isContrast
                        ? `
                    .picker-item-active {
                      font-weight: bold !important;
                      color: var(--contrast-white-black) !important;
                    }
                  `
                        : `.picker-item-active {
                      font-weight: bold !important;
                    }`;

                    styleEl.textContent += `.picker-item {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                    }`;
                    col.shadowRoot.prepend(styleEl);
                });
            });
        });
    }

    // FIXME: Remove after upgrading to Ionic 7+ - styles month/year picker for accessibility
    private applyMonthYearPickerStyles(retry = 0): void {
        const maxRetries = 5;

        // Use a small delay to ensure the datetime element is fully rendered
        setTimeout(() => {
            const datetimes = document.querySelectorAll('ion-datetime');
            let monthYearFound = false;

            datetimes.forEach((dt) => {
                const datetimeYear = dt.shadowRoot?.querySelector('.datetime-year');
                if (!datetimeYear) {
                    return;
                }

                monthYearFound = true;
                const pickerColumns = datetimeYear.querySelectorAll('ion-picker-column-internal');

                pickerColumns.forEach((col) => {
                    if (!col.shadowRoot) {
                        return;
                    }

                    const styleEl = document.createElement('style');
                    styleEl.textContent = `
                    .picker-item {
                        font-size: calc(0.875rem * min(max(var(--scale-factor, 1), var(--min-scale-factor, 1)), var(--max-scale-factor, 2.8))) !important;
                        overflow: visible !important;
                        white-space: normal !important;
                        line-height: normal !important;
                    }
                    .picker-item-active {
                        font-weight: bold !important;
                    }
                `;
                    col.shadowRoot.prepend(styleEl);
                });
            });

            if (!monthYearFound && retry < maxRetries) {
                this.applyMonthYearPickerStyles(retry + 1);
            }
        }, 100);
    }

    // FIXME: Remove after upgrading to Ionic 7+ and style with supplied CSS variables instead
    applyDynamicStyles = () => {
        const done = new Set();
        const pickerColumnsDone = new Set();

        const dtStyles = `
        .calendar-days-of-week {
            font-size: calc(0.75rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor))) !important;
        }
        .calendar-day {
            font-size: calc(1.25rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor))) !important;
        }
        ion-item, .datetime-time, .time-body {
            font-size: calc(1rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor))) !important;
        }
        ion-icon {
            font-size: calc(1.75rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor))) !important;
        }
        .datetime-year {
            display: flex !important;
            justify-content: center !important;
        }
        .datetime-year ion-picker-column-internal {
            width: auto !important;
            max-width: 50% !important;
        }
    `;

        // Styles for month/year picker items - these go INSIDE the picker-column-internal shadow roots
        const pickerItemStyles = `
        .picker-item {
            font-size: calc(0.875rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor))) !important;
            padding: calc(0.25rem * min(max(var(--scale-factor), var(--min-scale-factor)), var(--max-scale-factor))) !important;
            overflow: visible !important;
            white-space: normal !important;
            line-height: 34px !important;
        }
    `;

        const injectStyles = (el, styles, label = 'unknown') => {
            if (!el.shadowRoot) {
                console.warn(`[DatetimeStyles] No shadow root found for ${label}`, el);
                return;
            }
            const hasStyle = [...el.shadowRoot.querySelectorAll('style')].some((s) =>
                s.textContent.includes(styles.trim().split(' ')[0])
            );
            if (!hasStyle) {
                const styleEl = document.createElement('style');
                styleEl.textContent = styles;
                el.shadowRoot.appendChild(styleEl);
            }
        };

        const stylePickerColumns = (datetimeElement) => {
            if (!datetimeElement.shadowRoot) {
                return;
            }

            // Find the datetime-year container
            const datetimeYear = datetimeElement.shadowRoot.querySelector('.datetime-year');
            if (!datetimeYear) {
                console.warn('[DatetimeStyles] .datetime-year not found in datetime shadow root');
                return;
            }

            // Find all picker columns inside the month/year picker
            const pickerColumns = datetimeYear.querySelectorAll('ion-picker-column-internal');
            pickerColumns.forEach((col, index) => {
                if (!pickerColumnsDone.has(col)) {
                    pickerColumnsDone.add(col);
                    injectStyles(col, pickerItemStyles, `picker-column-${index}`);
                }
            });
        };

        this.mo = new MutationObserver(() => {
            const datetimes = document.querySelectorAll('ion-datetime');
            datetimes.forEach((el) => {
                if (!done.has(el)) {
                    done.add(el);
                    injectStyles(el, dtStyles, 'ion-datetime');
                }
                // Always check for picker columns as they may be added dynamically
                stylePickerColumns(el);
            });
        });

        this.mo.observe(document.body, { childList: true, subtree: true });
    };

    private findButtonInNestedShadows(root: ShadowRoot | null, selector: string): HTMLElement | null {
        if (!root) {
            return null;
        }

        const button = root.querySelector(selector);
        if (button) {
            return button as HTMLElement;
        }

        for (const el of Array.from(root.querySelectorAll('*')) as HTMLElement[]) {
            const shadow = el.shadowRoot;
            if (shadow) {
                const nestedButton = this.findButtonInNestedShadows(shadow, selector);
                if (nestedButton) {
                    return nestedButton;
                }
            }
        }
        return null;
    }

    closeModal(): void {
        this.timeModal.dismiss(null, 'cancel');
    }

    writeValue(obj: DepartureArrivalTime): void {
        this.externalDepartureArrivalVal = obj;
        this.internalDepartureArrivalVal = { ...obj };
    }

    registerOnChange(fn: (val: DepartureArrivalTime) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    confirmChanges() {
        this.markAsTouched();
        if (!this.disabled) {
            this.setExternalValues();
            this.onChange(this.externalDepartureArrivalVal);
            this.closeModal();
        }
    }

    resetToCurrent() {
        this.markAsTouched();
        if (!this.disabled) {
            if (this.resetFilter) {
                this.internalDepartureArrivalVal.datetime = null;
            } else if (this.dateOnly) {
                this.internalDepartureArrivalVal.datetime = Temporal.Now.plainDateISO(this.timezone).toString();
            } else {
                this.internalDepartureArrivalVal.datetime = Temporal.Now.zonedDateTimeISO(this.timezone).toString({
                    smallestUnit: 'second',
                    timeZoneName: 'never'
                });
            }
            this.confirmChanges();
        }
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    private setCurrentDateTime() {
        try {
            if (this.dateOnly) {
                this.internalDepartureArrivalVal.datetime = Temporal.Now.plainDateISO(this.timezone).toString();
            } else {
                const plainDateTime = Temporal.Now.plainDateTimeISO(this.timezone);
                this.internalDepartureArrivalVal.datetime = plainDateTime.toString({ smallestUnit: 'minute' });
            }
        } catch (error) {
            console.error('Error setting current datetime:', error);
            const now = new Date();
            if (this.dateOnly) {
                this.internalDepartureArrivalVal.datetime = now.toISOString().split('T')[0];
            } else {
                const plainDateTime = Temporal.PlainDateTime.from(now.toISOString().slice(0, -1));
                this.internalDepartureArrivalVal.datetime = plainDateTime.toString({ smallestUnit: 'minute' });
            }
        }
    }

    willPresent() {
        this.setInternalValues();
        this.pollForButtons();

        const isScaled = document.documentElement.classList.contains('scaled');
        if (isScaled) {
            this.applyDynamicStyles();
        }
        if (Capacitor.getPlatform() === 'ios') {
            this.addEventListenerForTimeSelectionBtn();
        }
    }

    private setExternalValues() {
        if (!this.externalDepartureArrivalVal) {
            this.externalDepartureArrivalVal = {} as DepartureArrivalTime;
        }

        if (!this.internalDepartureArrivalVal || !this.internalDepartureArrivalVal.datetime) {
            this.externalDepartureArrivalVal.datetime = null;
            this.externalDepartureArrivalVal.type = null;
            return;
        }

        this.externalDepartureArrivalVal.type = this.internalDepartureArrivalVal.type;

        const datetime = Temporal.PlainDateTime.from(this.internalDepartureArrivalVal.datetime).toZonedDateTime(
            this.timezone
        );

        if (this.triggerId === 'explore-stop-details-time') {
            const zonedDateTime = Temporal.PlainDateTime.from(
                this.internalDepartureArrivalVal.datetime
            ).toZonedDateTime(this.timezone);
            this.externalDepartureArrivalVal.datetime = zonedDateTime.toString({
                smallestUnit: 'second',
                timeZoneName: 'never'
            });
        } else {
            this.externalDepartureArrivalVal.datetime = this.dateOnly
                ? datetime.toPlainDate().toString()
                : datetime.toInstant().toString({ smallestUnit: 'minute', timeZone: this.timezone });
        }
    }

    private setInternalValues() {
        this.internalDepartureArrivalVal = this.internalDepartureArrivalVal ?? ({} as DepartureArrivalTime);

        this.internalDepartureArrivalVal.type = this.externalDepartureArrivalVal?.type;

        if (this.dateOnly && this.externalDepartureArrivalVal?.datetime) {
            try {
                this.internalDepartureArrivalVal.datetime = Temporal.PlainDate.from(
                    this.externalDepartureArrivalVal.datetime
                ).toString();
            } catch (error) {
                this.setCurrentDateTime();
            }
        } else if (this.externalDepartureArrivalVal?.datetime) {
            try {
                let zonedDateTime;

                if (
                    this.externalDepartureArrivalVal.datetime.includes('+') ||
                    this.externalDepartureArrivalVal.datetime.includes('Z')
                ) {
                    try {
                        zonedDateTime = Temporal.Instant.from(
                            this.externalDepartureArrivalVal.datetime
                        ).toZonedDateTimeISO(this.timezone);
                    } catch {
                        zonedDateTime = Temporal.ZonedDateTime.from(
                            this.externalDepartureArrivalVal.datetime
                        ).withTimeZone(this.timezone);
                    }
                } else {
                    const datetimeStr = this.externalDepartureArrivalVal.datetime;
                    const timezone = this.timezone;

                    try {
                        const plainDateTime = Temporal.PlainDateTime.from(datetimeStr);

                        zonedDateTime = plainDateTime.toZonedDateTime(timezone);
                    } catch (err) {
                        throw new Error('Invalid date string');
                    }
                }

                const plainDateTime = zonedDateTime.toPlainDateTime();
                this.internalDepartureArrivalVal.datetime = plainDateTime.toString({ smallestUnit: 'minute' });
            } catch (error) {
                console.error('Error parsing datetime value:', error);
                this.setCurrentDateTime();
            }
        } else {
            this.setCurrentDateTime();
        }
    }

    ngOnDestroy() {
        this.popoverObserver?.disconnect();
        this.popoverObserver = null;
        this.mo?.disconnect();
        this.mo = null;
        super.ngOnDestroy();
    }

    /**
     * Adds a close button to the time picker popover and handles scroll position preservation.
     *
     * This method addresses the issue where opening the time picker popover (the scrollable
     * wheel for hours/minutes) causes the main datetime modal to scroll to the top. When the
     * popover is dismissed (either by clicking the close button or clicking outside), the
     * modal's scroll position needs to be restored to where the user was before.
     *
     * The solution:
     * 1. Save the modal's scroll position when the time picker popover appears
     * 2. Set up a MutationObserver to watch for when the popover is removed from the DOM
     * 3. When popover dismissal is detected, restore the saved scroll position using
     *    requestAnimationFrame to ensure it happens after browser rendering completes
     *
     * This handles both dismissal methods: clicking the X button and clicking outside the popover.
     */
    addCloseButton(): void {
        this.popoverObserver?.disconnect();

        this.popoverObserver = new MutationObserver((mutationsList, observer) => {
            const popover = document.body.querySelector('ion-popover') as HTMLElement;
            if (popover) {
                // Save scroll position when popover appears - target .modal-wrapper which is the scrollable container
                const modal = document.querySelector('ion-modal.time-modal');
                const modalWrapper = modal?.querySelector('.modal-wrapper') as HTMLElement;

                if (modalWrapper) {
                    this.modalScrollPosition = modalWrapper.scrollTop;
                }

                let contrastSubscription: Subscription;

                // Set up observer to watch for popover dismissal (by any means)
                const dismissObserver = new MutationObserver(() => {
                    const popoverStillExists = document.body.querySelector('ion-popover');
                    if (!popoverStillExists) {
                        // Popover dismissed, restore scroll to .modal-wrapper
                        requestAnimationFrame(() => {
                            const modal = document.querySelector('ion-modal.time-modal');
                            const modalWrapper = modal?.querySelector('.modal-wrapper') as HTMLElement;

                            if (modalWrapper && this.modalScrollPosition !== undefined) {
                                modalWrapper.scrollTop = this.modalScrollPosition;
                            }
                        });

                        if (contrastSubscription) {
                            contrastSubscription.unsubscribe();
                        }
                        dismissObserver.disconnect();
                    }
                });

                dismissObserver.observe(document.body, { childList: true, subtree: true });

                const content = popover.querySelector('ion-picker-internal') as HTMLElement;
                if (content) {
                    const wrapperId = 'time-picker-popover-wrapper';
                    let wrapper = popover.querySelector(`#${wrapperId}`) as HTMLElement;
                    if (!wrapper) {
                        wrapper = document.createElement('div');
                        wrapper.id = wrapperId;
                        this.applyWrapperStyles(wrapper);
                        popover.appendChild(wrapper);
                    }

                    const labelId = 'time-picker-select-label';
                    let timePickerLabel = wrapper.querySelector(`#${labelId}`) as HTMLElement;
                    if (!timePickerLabel) {
                        timePickerLabel = document.createElement('div');
                        timePickerLabel.id = labelId;
                        wrapper.appendChild(timePickerLabel);
                    }

                    const labelEl = timePickerLabel as HTMLElement;
                    this.setupLabelAttributes(labelEl);
                    this.applyStaticLabelStyles(labelEl);

                    if (content.parentElement !== wrapper) {
                        wrapper.appendChild(content);
                    }

                    content.setAttribute('aria-label', this.translate.instant('DATETIME_MODAL_SELECT_TIME'));
                    content.setAttribute('role', 'dialog');
                    this.applyStaticContentStyles(content);

                    // Check if a close button is already present
                    const existingButton = wrapper.querySelector('ion-button');
                    if (!existingButton) {
                        this.createAndAppendCloseButton(wrapper);
                    } else {
                        this.timePickerCloseBtn = existingButton as HTMLIonButtonElement;
                    }

                    this.setupPickerColumns(content);

                    setTimeout(() => {
                        labelEl.focus();
                    }, 200);

                    contrastSubscription = this.applicationFacade.highContrast$.subscribe((isContrast) => {
                        this.updateContrastTheme(isContrast, wrapper, popover, labelEl);
                    });

                    observer.disconnect();
                    this.popoverObserver = null;
                }
            }
        });

        this.popoverObserver.observe(document.body, { childList: true, subtree: true });
    }

    private applyWrapperStyles(wrapper: HTMLElement): void {
        wrapper.style.display = 'flex';
        wrapper.style.flexDirection = 'column';
        wrapper.style.height = '100%';
        wrapper.style.width = '100%';
        wrapper.style.position = 'relative';
        wrapper.style.overflow = 'hidden';
    }

    private setupLabelAttributes(labelEl: HTMLElement): void {
        labelEl.textContent = this.translate.instant('DATETIME_MODAL_SELECT_TIME');
        labelEl.setAttribute('role', 'heading');
        labelEl.setAttribute('aria-level', '2');
        labelEl.setAttribute('tabindex', '-1');
    }

    private applyStaticLabelStyles(labelEl: HTMLElement): void {
        labelEl.style.position = 'sticky';
        labelEl.style.top = '0';
        labelEl.style.left = '0';
        labelEl.style.right = '0';
        labelEl.style.height = '3.25rem';
        labelEl.style.display = 'flex';
        labelEl.style.alignItems = 'center';
        labelEl.style.marginBottom = '-0.75rem';
        labelEl.style.justifyContent = 'center';
        labelEl.style.textAlign = 'center';
        labelEl.style.fontWeight = '400';
        labelEl.style.lineHeight = '2rem';
        labelEl.style.zIndex = '1001';
        labelEl.style.flexShrink = '0';
    }

    private applyStaticContentStyles(content: HTMLElement): void {
        content.style.position = 'relative';
        content.style.top = '0';
        content.style.left = '0';
        content.style.right = '0';
        content.style.bottom = '0';
        content.style.flex = '1';
        content.style.height = 'auto';
    }

    private createAndAppendCloseButton(wrapper: HTMLElement): void {
        this.timePickerCloseBtn = document.createElement('ion-button');
        this.timePickerCloseBtn.fill = 'clear';
        this.timePickerCloseBtn.size = 'small';
        this.timePickerCloseBtn.style.position = 'absolute';
        this.timePickerCloseBtn.style.top = '0.5rem';
        this.timePickerCloseBtn.style.transform = 'translateY(-1.5px)';
        this.timePickerCloseBtn.style.right = '0';
        this.timePickerCloseBtn.style.height = '2.25rem';
        this.timePickerCloseBtn.style.zIndex = '1002';
        this.timePickerCloseBtn.classList.add('w-16', 'date-picker-close-button');

        const icon = document.createElement('ion-icon');
        icon.name = 'close';
        icon.slot = 'icon-only';
        this.timePickerCloseBtn.appendChild(icon);

        this.timePickerCloseBtn.setAttribute('aria-label', this.translate.instant('CLOSE'));
        this.timePickerCloseBtn.addEventListener('click', () => {
            this.setTimeBodyAriaLabel();
            this.handleClick();
        });

        wrapper.appendChild(this.timePickerCloseBtn);
    }

    private setupPickerColumns(content: HTMLElement): void {
        const columns = content.querySelectorAll('ion-picker-column-internal');
        columns.forEach((col) => {
            col.setAttribute('role', 'slider');
            col.setAttribute('aria-orientation', 'vertical');

            const value = (col as HTMLIonPickerColumnInternalElement).value;
            if (value !== undefined && value !== null) {
                col.setAttribute('aria-valuetext', value.toString());
            } else {
                const activeItem = col.shadowRoot?.querySelector('.picker-item-active');
                if (activeItem) {
                    col.setAttribute('aria-valuetext', activeItem.textContent?.trim() || '');
                }
            }

            col.addEventListener('ionChange', (ev: CustomEvent) => {
                const value = ev.detail.value;
                col.setAttribute('aria-valuetext', value?.toString() || '');
            });
        });

        if (columns.length === 2) {
            columns[0].setAttribute('aria-label', this.translate.instant('HOURS'));
            columns[1].setAttribute('aria-label', this.translate.instant('MINUTES'));
        }
    }

    private updateContrastTheme(
        isContrast: boolean,
        wrapper: HTMLElement,
        popover: HTMLElement,
        labelEl: HTMLElement
    ): void {
        const bgColor = isContrast ? 'black' : 'white';
        const textColor = isContrast ? 'var(--contrast-yellow, #ffff00)' : 'rgba(0, 0, 0, 0.7)';

        if (wrapper) wrapper.style.backgroundColor = bgColor;
        if (popover) popover.style.setProperty('--background', bgColor);

        labelEl.style.backgroundColor = bgColor;
        labelEl.style.color = textColor;

        if (this.timePickerCloseBtn) {
            if (isContrast) {
                this.timePickerCloseBtn.style.color = 'var(--contrast-yellow, #ffff00)';
            } else {
                this.timePickerCloseBtn.style.removeProperty('color');
            }
        }
    }

    private handleClick() {
        const popover = document.body.querySelector('ion-popover');

        // Set up an observer to watch for when popover is removed
        const dismissObserver = new MutationObserver(() => {
            const popoverStillExists = document.body.querySelector('ion-popover');
            if (!popoverStillExists) {
                // Popover has been removed, restore scroll position to .modal-wrapper
                requestAnimationFrame(() => {
                    const modal = document.querySelector('ion-modal.time-modal');
                    const modalWrapper = modal?.querySelector('.modal-wrapper') as HTMLElement;

                    if (modalWrapper && this.modalScrollPosition !== undefined) {
                        modalWrapper.scrollTop = this.modalScrollPosition;
                    }
                });
                dismissObserver.disconnect();
            }
        });

        dismissObserver.observe(document.body, { childList: true, subtree: true });

        popover.dismiss();
    }

    willDismiss() {
        const content = document.querySelector('ion-content');
        const footer = document.querySelector('ion-footer');
        const monthYearButton = this.getMonthYearButton();

        this.selectTimeButton?.removeEventListener('click', this.addCloseButton);
        this.timePickerCloseBtn?.removeEventListener('click', this.handleClick);
        content?.removeAttribute('inert');
        footer?.removeAttribute('inert');
        monthYearButton?.removeEventListener('click', this.boundMonthYearEventListener);
    }

    ionModalDidPresents(): void {
        const content = document.querySelector('ion-content');
        const footer = document.querySelector('ion-footer');
        if (content) {
            content.setAttribute('inert', '');
        }
        if (footer) {
            footer.setAttribute('inert', '');
        }
        const monthYearButton = this.getMonthYearButton();
        monthYearButton?.setAttribute('aria-expanded', 'false');
        monthYearButton?.addEventListener('click', this.boundMonthYearEventListener);

        const ionDatetimeShadowRoot = this.getDatetimeElement()?.shadowRoot;
        if (!ionDatetimeShadowRoot) {
            return;
        }
        const styles = document.createElement('style');
        styles.innerText = `
                .calendar-day.calendar-day-today {
                    font-weight: bold;
                    color: var(--ion-color-primary) !important;
                }
                .calendar-month .calendar-day.calendar-day-active .calendar-day-highlight {
                    background-color: var(--ion-color-primary) !important;
                    opacity: 1 !important;
                }
                .calendar-day.calendar-day-active, .calendar-day.calendar-day-today.calendar-day-active {
                    color: var(--ion-color-primary-contrast) !important;
                }
                .calendar-days-of-week,
                .calendar-day:disabled {
                    color: var(--text-contrast-gray) !important;
                }
                .calendar-action-buttons {
                    flex-wrap: wrap;
                }
                .calendar-action-buttons .calendar-next-prev {
                    flex: 0 0 auto;
                }`;
        if (this.env.SHOW_SEPARATOR_BETWEEN_MONTH_AND_TIME_ON_CALENDAR) {
            styles.innerText += `
                .calendar-month {
                    border-bottom-width: 1px;
                    border-bottom-style: solid;
                    border-bottom-color: rgba(0, 0, 0, 0.1);
                }
            `;
        }
        ionDatetimeShadowRoot.appendChild(styles);

        const navButtons = ionDatetimeShadowRoot.querySelector('.calendar-next-prev')?.children?.[0]?.children;
        const prevIonButton = navButtons?.[0] as HTMLElement | undefined;
        const nextIonButton = navButtons?.[1] as HTMLElement | undefined;

        prevIonButton?.shadowRoot?.children?.[0]?.setAttribute(
            'ariaLabel',
            this.translate.instant('DATE_PICKER_PREVIOUS_MONTH')
        );
        nextIonButton?.shadowRoot?.children?.[0]?.setAttribute(
            'ariaLabel',
            this.translate.instant('DATE_PICKER_NEXT_MONTH')
        );

        this.applyTimePickerStyles();
        if (this.dateOnly) {
            this.applyMonthYearPickerStyles();
        }
    }

    private addEventListenerForTimeSelectionBtn() {
        const ionDateTime = this.getDatetimeElement();
        if (ionDateTime) {
            const observer = new MutationObserver(() => {
                if (ionDateTime.shadowRoot) {
                    this.selectTimeButton = ionDateTime.shadowRoot.querySelector('.time-body');
                    if (this.selectTimeButton) {
                        this.selectTimeButton.addEventListener('click', () => this.addCloseButton());
                        observer.disconnect();
                    }
                }
            });

            observer.observe(ionDateTime, { childList: true, subtree: true });
        } else {
            console.error('ion-datetime not found');
        }
    }

    private monthYearEventListener() {
        const monthYearButton = this.getMonthYearButton();
        if (!monthYearButton) {
            return;
        }
        const currentExpandedValue = monthYearButton.getAttribute('aria-expanded') === 'true';
        monthYearButton.setAttribute('aria-expanded', (!currentExpandedValue).toString());
    }

    private getDatetimeElement(): HTMLElement | null {
        const modal = document.querySelector('ion-modal.time-modal');
        return (
            modal?.querySelector<HTMLElement>('ridango-datetime-picker ion-datetime') ??
            modal?.querySelector<HTMLElement>('ion-datetime') ??
            document.querySelector<HTMLElement>('ion-datetime')
        );
    }

    private getMonthYearButton(): HTMLElement | null {
        const ionDatetimeShadowRoot = this.getDatetimeElement()?.shadowRoot;
        const ionItem = ionDatetimeShadowRoot?.querySelector('ion-item') as HTMLElement | null;
        const ionItemShadowRoot = ionItem?.shadowRoot;
        return ionItemShadowRoot?.querySelector<HTMLElement>('.item-native') ?? null;
    }
}
